import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import type { Profile } from "@/services/api/companies";
import { formatExperienceMonths } from "./utils";
import { COLUMN_LABELS } from "./columnConfig";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/shared/ui/hover-card";

export function useCompanyStaffColumns(): ColumnDef<Profile>[] {
  return useMemo<ColumnDef<Profile>[]>(() => {
    const createSortableHeader = (label: string) => {
      return ({ column }: { column: any }) => {
        const canSort = column.getCanSort();
        const sortDirection = column.getIsSorted();

        return (
          <div
            className="flex items-center cursor-pointer hover:opacity-80 select-none"
            onClick={column.getToggleSortingHandler()}
          >
            {label}
            {canSort && (
              <span className="ml-1">
                {sortDirection === "asc" ? (
                  <ArrowUp className="h-3 w-3" />
                ) : sortDirection === "desc" ? (
                  <ArrowDown className="h-3 w-3" />
                ) : (
                  <ArrowUpDown className="h-3 w-3 opacity-50" />
                )}
              </span>
            )}
          </div>
        );
      };
    };

    const columns: ColumnDef<Profile>[] = [
      {
        accessorKey: "firstName",
        id: "firstName",
        header: createSortableHeader(COLUMN_LABELS.firstName),
        enableSorting: true,
        size: 170,
        minSize: 100,
        cell: (info) => (
          <span className="text-sm text-ink-900">
            {(info.getValue() as string) || "—"}
          </span>
        ),
      },
      {
        accessorKey: "lastName",
        id: "lastName",
        header: createSortableHeader(COLUMN_LABELS.lastName),
        enableSorting: true,
        size: 170,
        minSize: 100,
        cell: (info) => (
          <span className="text-sm text-ink-900">
            {(info.getValue() as string) || "—"}
          </span>
        ),
      },
      {
        accessorKey: "title",
        id: "title",
        header: createSortableHeader(COLUMN_LABELS.title),
        enableSorting: true,
        size: 250,
        minSize: 200,
        maxSize: 350,
        cell: (info) => (
          <span
            className="block max-w-[400px] truncate text-sm text-ink-600"
            title={(info.getValue() as string) || "—"}
          >
            {(info.getValue() as string) || "—"}
          </span>
        ),
      },
      {
        accessorKey: "linkedInUrl",
        id: "url",
        header: createSortableHeader(COLUMN_LABELS.url),
        enableSorting: false,
        size: 120,
        minSize: 100,
        cell: (info) => {
          const url = info.getValue() as string | undefined;
          if (!url) {
            return <span className="text-sm text-ink-400">—</span>;
          }

          const row = info.row.original;
          const name =
            [row.firstName, row.lastName].filter(Boolean).join(" ") ||
            "Unknown";

          return (
            <HoverCard openDelay={300} closeDelay={100}>
              <HoverCardTrigger asChild>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-imperial-600 hover:underline inline-flex items-center gap-1"
                >
                  View Profile
                  <ExternalLink className="h-3 w-3" />
                </a>
              </HoverCardTrigger>
              <HoverCardContent
                side="right"
                align="start"
                className="w-[28rem] p-6"
              >
                <div className="space-y-4">
                  <div>
                    <p className="text-lg font-semibold text-white">{name}</p>
                    {row.title && (
                      <p className="text-base text-white/60 mt-1 line-clamp-2">
                        {row.title}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-x-5 gap-y-2.5 text-base">
                    {row.currentCompany && (
                      <>
                        <span className="text-white/50">Company</span>
                        <span className="text-white/80">
                          {row.currentCompany}
                        </span>
                      </>
                    )}
                    {row.location && (
                      <>
                        <span className="text-white/50">Location</span>
                        <span className="text-white/80">{row.location}</span>
                      </>
                    )}
                    {row.seniority && (
                      <>
                        <span className="text-white/50">Seniority</span>
                        <span className="text-white/80">{row.seniority}</span>
                      </>
                    )}
                    {row.department && (
                      <>
                        <span className="text-white/50">Department</span>
                        <span className="text-white/80">{row.department}</span>
                      </>
                    )}
                    {row.totalExperienceMonths > 0 && (
                      <>
                        <span className="text-white/50">Total Exp</span>
                        <span className="text-white/80">
                          {formatExperienceMonths(row.totalExperienceMonths)}
                        </span>
                      </>
                    )}
                    {row.f1ExperienceMonths != null &&
                      row.f1ExperienceMonths > 0 && (
                        <>
                          <span className="text-white/50">F1 Exp</span>
                          <span className="text-white/80">
                            {formatExperienceMonths(row.f1ExperienceMonths)}
                          </span>
                        </>
                      )}
                  </div>

                  {row.education && row.education.length > 0 && (
                    <div>
                      <p className="text-base font-medium text-white/50 mb-1.5">
                        Education
                      </p>
                      <div className="space-y-0.5">
                        {row.education.slice(0, 2).map((edu, i) => (
                          <p
                            key={i}
                            className="text-base text-white/70 line-clamp-1"
                          >
                            {[edu.institutionName, edu.degree]
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </p>
                        ))}
                        {row.education.length > 2 && (
                          <p className="text-base text-white/40">
                            +{row.education.length - 2} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {row.companiesWorkedAt &&
                    row.companiesWorkedAt.length > 0 && (
                      <div>
                        <p className="text-base font-medium text-white/50 mb-1.5">
                          Companies Worked At
                        </p>
                        <div className="space-y-0.5">
                          {row.companiesWorkedAt.slice(0, 4).map((c, i) => (
                            <p
                              key={i}
                              className="text-base text-white/70 line-clamp-1"
                            >
                              {c.companyName || "—"}
                              {(c.dateFromYear || c.dateToYear) && (
                                <span className="text-white/40">
                                  {" "}
                                  (
                                  {c.dateFromYear && !c.dateToYear
                                    ? `${c.dateFromYear} - current`
                                    : [c.dateFromYear, c.dateToYear]
                                        .filter(Boolean)
                                        .join(" - ")}
                                  )
                                </span>
                              )}
                            </p>
                          ))}
                          {row.companiesWorkedAt.length > 4 && (
                            <p className="text-base text-white/40">
                              +{row.companiesWorkedAt.length - 4} more
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </HoverCardContent>
            </HoverCard>
          );
        },
      },
      {
        accessorKey: "location",
        id: "location",
        header: createSortableHeader(COLUMN_LABELS.location),
        enableSorting: true,
        size: 150,
        minSize: 120,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as string) || "—"}
          </span>
        ),
      },
      {
        accessorKey: "department",
        id: "department",
        header: createSortableHeader(COLUMN_LABELS.department),
        enableSorting: true,
        size: 150,
        minSize: 120,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as string) || "—"}
          </span>
        ),
      },
      {
        accessorKey: "country",
        id: "country",
        header: createSortableHeader(COLUMN_LABELS.country),
        enableSorting: true,
        size: 120,
        minSize: 100,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as string) || "—"}
          </span>
        ),
      },
      {
        accessorKey: "seniority",
        id: "seniority",
        header: createSortableHeader(COLUMN_LABELS.seniority),
        enableSorting: true,
        size: 120,
        minSize: 100,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as string) || "—"}
          </span>
        ),
      },
      {
        id: "totalExperienceMonths",
        accessorFn: (row) => row.totalExperienceMonths,
        header: createSortableHeader(COLUMN_LABELS.totalExperienceMonths),
        enableSorting: true,
        size: 150,
        minSize: 120,
        cell: (info) => {
          const months = info.row.original.totalExperienceMonths ?? null;
          return (
            <span className="text-sm text-ink-600">
              {formatExperienceMonths(months)}
            </span>
          );
        },
      },
      {
        accessorKey: "currentCompany",
        id: "currentCompany",
        header: createSortableHeader(COLUMN_LABELS.currentCompany),
        enableSorting: true,
        size: 180,
        minSize: 150,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as string) || "—"}
          </span>
        ),
      },
      {
        id: "f1ExperienceMonths",
        accessorFn: (row) => row.f1ExperienceMonths,
        header: createSortableHeader(COLUMN_LABELS.f1ExperienceMonths),
        enableSorting: true,
        size: 140,
        minSize: 120,
        cell: (info) => {
          const months = info.row.original.f1ExperienceMonths ?? null;
          return (
            <span className="text-sm text-ink-600">
              {formatExperienceMonths(months)}
            </span>
          );
        },
      },
      {
        accessorKey: "experienceCount",
        id: "experienceCount",
        header: createSortableHeader(COLUMN_LABELS.experienceCount),
        enableSorting: true,
        size: 150,
        minSize: 120,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as number) || 0}
          </span>
        ),
      },
      {
        accessorKey: "hasF1V8Experience",
        id: "hasF1V8Experience",
        header: createSortableHeader(COLUMN_LABELS.hasF1V8Experience),
        enableSorting: true,
        size: 150,
        minSize: 120,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as boolean) ? "Yes" : "No"}
          </span>
        ),
      },
      {
        accessorKey: "hasFiaExperience",
        id: "hasFiaExperience",
        header: createSortableHeader(COLUMN_LABELS.hasFiaExperience),
        enableSorting: true,
        size: 140,
        minSize: 120,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as boolean) ? "Yes" : "No"}
          </span>
        ),
      },
      {
        accessorKey: "hasAusEducation",
        id: "hasAusEducation",
        header: createSortableHeader(COLUMN_LABELS.hasAusEducation),
        enableSorting: true,
        size: 140,
        minSize: 120,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as boolean) ? "Yes" : "No"}
          </span>
        ),
      },
      {
        accessorKey: "hasUsEducation",
        id: "hasUsEducation",
        header: createSortableHeader(COLUMN_LABELS.hasUsEducation),
        enableSorting: true,
        size: 140,
        minSize: 120,
        cell: (info) => (
          <span className="text-sm text-ink-600">
            {(info.getValue() as boolean) ? "Yes" : "No"}
          </span>
        ),
      },
      {
        id: "pumExperienceMonths",
        accessorFn: (row) => row.pumExperienceMonths,
        header: createSortableHeader(COLUMN_LABELS.pumExperienceMonths),
        enableSorting: true,
        size: 150,
        minSize: 120,
        cell: (info) => {
          const months = info.row.original.pumExperienceMonths ?? null;
          return (
            <span className="text-sm text-ink-600">
              {formatExperienceMonths(months)}
            </span>
          );
        },
      },
      {
        id: "australiaExperienceMonths",
        accessorFn: (row) => row.australiaExperienceMonths,
        header: createSortableHeader(COLUMN_LABELS.australiaExperienceMonths),
        enableSorting: true,
        size: 180,
        minSize: 150,
        cell: (info) => {
          const months = info.row.original.australiaExperienceMonths ?? null;
          return (
            <span className="text-sm text-ink-600">
              {formatExperienceMonths(months)}
            </span>
          );
        },
      },
      {
        id: "usaExperienceMonths",
        accessorFn: (row) => row.usaExperienceMonths,
        header: createSortableHeader(COLUMN_LABELS.usaExperienceMonths),
        enableSorting: true,
        size: 150,
        minSize: 120,
        cell: (info) => {
          const months = info.row.original.usaExperienceMonths ?? null;
          return (
            <span className="text-sm text-ink-600">
              {formatExperienceMonths(months)}
            </span>
          );
        },
      },
      {
        accessorKey: "fakeProbability",
        id: "fakeProbability",
        header: createSortableHeader(COLUMN_LABELS.fakeProbability),
        enableSorting: true,
        size: 140,
        minSize: 120,
        cell: (info) => {
          const value = info.getValue() as number | null | undefined;
          if (value == null) {
            return <span className="text-sm text-ink-400">—</span>;
          }

          let colorClass = "text-emerald-600";
          let label = "Low";
          if (value >= 80) {
            colorClass = "text-red-600";
            label = "High";
          } else if (value >= 50) {
            colorClass = "text-amber-600";
            label = "Medium";
          }

          return (
            <span className={`text-sm font-medium ${colorClass}`}>
              {value}% ({label})
            </span>
          );
        },
      },
      {
        id: "education",
        accessorFn: (row) => row.education,
        header: createSortableHeader(COLUMN_LABELS.education),
        enableSorting: true,
        size: 300,
        minSize: 200,
        maxSize: 450,
        cell: (info) => {
          const education = info.row.original.education;
          if (!education || education.length === 0) {
            return <span className="text-sm text-ink-400">—</span>;
          }

          return (
            <div className="space-y-1.5 py-1">
              {education.map((edu, index) => {
                const parts: string[] = [];

                if (edu.institutionName) {
                  parts.push(edu.institutionName);
                }

                if (edu.degree) {
                  parts.push(edu.degree);
                }

                const yearRange: string[] = [];
                if (edu.dateFromYear) {
                  yearRange.push(edu.dateFromYear.toString());
                }
                if (edu.dateToYear) {
                  yearRange.push(edu.dateToYear.toString());
                }

                const yearStr =
                  yearRange.length > 0 ? ` (${yearRange.join(" - ")})` : "";

                const mainText =
                  parts.length > 0
                    ? parts.join(", ") + yearStr
                    : "Education entry";

                return (
                  <div
                    key={index}
                    className="text-sm text-ink-600 leading-relaxed"
                    title={mainText}
                  >
                    {mainText}
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        id: "companiesWorkedAt",
        accessorFn: (row) => row.companiesWorkedAt,
        header: createSortableHeader(COLUMN_LABELS.companiesWorkedAt),
        enableSorting: false,
        size: 300,
        minSize: 200,
        maxSize: 450,
        cell: (info) => {
          const companies = info.row.original.companiesWorkedAt;
          if (!companies || companies.length === 0) {
            return <span className="text-sm text-ink-400">—</span>;
          }

          return (
            <div className="space-y-1.5 py-1">
              {companies.map((company, index) => {
                const parts: string[] = [];

                if (company.companyName) {
                  parts.push(company.companyName);
                }

                const yearRange: string[] = [];
                if (company.dateFromYear) {
                  yearRange.push(company.dateFromYear.toString());
                }
                if (company.dateToYear) {
                  yearRange.push(company.dateToYear.toString());
                }

                const yearStr =
                  yearRange.length > 0 ? ` (${yearRange.join(" - ")})` : "";

                const mainText =
                  parts.length > 0
                    ? parts.join(", ") + yearStr
                    : "Company entry";

                return (
                  <div
                    key={index}
                    className="text-sm text-ink-600 leading-relaxed"
                    title={mainText}
                  >
                    {mainText}
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        id: "LastSyncedAt",
        accessorFn: (row) => row.lastSyncedAt,
        header: createSortableHeader(COLUMN_LABELS.LastSyncedAt),
        enableSorting: true,
        size: 140,
        minSize: 120,
        cell: (info) => {
          const date = info.row.original.lastSyncedAt;
          return (
            <span className="text-sm text-ink-600">
              {date ? new Date(date).toLocaleDateString() : "—"}
            </span>
          );
        },
      },
    ];

    return columns;
  }, []);
}
